import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getDropdownOptions } from '../services/settingsService';

export default function FilterSidebar({ products = [], onFilterChange, collectionFilter, onClearCollectionFilter }) {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [closing, setClosing] = useState(false);

  // Calculate min and max prices from products
  const { minPrice, maxPrice } = useMemo(() => {
    if (!products || products.length === 0) {
      return { minPrice: 0, maxPrice: 4999 };
    }
    const prices = products.map(p => p.discountedPrice || p.mrp || p.price || 0).filter(p => p > 0);
    return {
      minPrice: Math.min(...prices) || 0,
      maxPrice: Math.max(...prices) || 4999
    };
  }, [products]);

  // Filter states
  const [filters, setFilters] = useState({
    categories: [],
    colours: [],
    sleeves: [],
    length: [],
    print: [],
    pattern: [],
    material: [],
    priceRange: [minPrice, maxPrice],
    discountRange: [0, 100],
    collection: []
  });

  // Dynamic filter options from backend
  const [filterOptions, setFilterOptions] = useState({
    colours: [],
    sleeves: [],
    length: [],
    print: [],
    pattern: [],
    material: [],
    collection: [],
    productType: [],
    storeCategories: []
  });

  // Collapsible sections state
  const [openSections, setOpenSections] = useState({
    category: false,
    colour: false,
    sleeves: false,
    length: false,
    print: false,
    pattern: false,
    material: false,
    price: false,
    discount: false,
    collection: false
  });

  // Fetch dropdown options from backend
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const options = await getDropdownOptions();
        setFilterOptions({
          colours: options.color || [],
          sleeves: options.sleeves || [],
          length: options.length || [],
          print: options.fabricPrint || [],
          pattern: options.fabricPattern || [],
          material: options.fabricType || [],
          collection: options.collection || [],
          productType: options.productType || [],
          storeCategories: options.storeCategories || []
        });
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };

    fetchOptions();
  }, []);

  // Inject lightweight CSS to standardize checkbox and range appearance
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('filter-input-styles')) return;
    const style = document.createElement('style');
    style.id = 'filter-input-styles';
    style.innerHTML = `
      /* Checkbox: fixed size to avoid disproportion across columns */
      input[type="checkbox"].custom-checkbox {
        -webkit-appearance: none;
        appearance: none;
        width: 16px;
        height: 16px;
        min-width: 16px;
        min-height: 16px;
        border: 1px solid #000;
        border-radius: 0;
        background: #fff;
        box-shadow: none;
        display: inline-block;
        flex-shrink: 0;
        vertical-align: middle;
      }
      input[type="checkbox"].custom-checkbox:checked {
        background: #000;
      }

      /* Range: thicker track, smaller rectangular thumb, no shadow */
      input[type="range"].custom-range {
        -webkit-appearance: none;
        appearance: none;
        height: 6px;
        background: #e5e7eb;
        border-radius: 0;
      }
      input[type="range"].custom-range::-webkit-slider-runnable-track {
        height: 6px;
        background: transparent;
      }
      input[type="range"].custom-range::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 12px;
        height: 16px;
        background: #000;
        border-radius: 0;
        margin-top: -5px; /* center thumb on track */
        box-shadow: none;
        border: none;
      }
      input[type="range"].custom-range::-moz-range-thumb {
        width: 12px;
        height: 16px;
        background: #000;
        border-radius: 0;
        border: none;
        box-shadow: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById('filter-input-styles');
      if (el) el.remove();
    };
  }, []);

  // Update price range when min/max prices change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      priceRange: [minPrice, maxPrice]
    }));
  }, [minPrice, maxPrice]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      document.body.style.setProperty('overflow', 'hidden', 'important');
    } else {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    }

    return () => {
      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
    };
  }, [isOpen]);

  // Auto-select categories when collection filter is set from URL
  // Also depends on products so children get selected once products load
  useEffect(() => {
    if (!collectionFilter) return;
    if (collectionFilter === 'Roz Roz' || collectionFilter === 'Kuch Roz') return;

    // Collect all children (productTypes) belonging to this parent storeCategory
    const children = [];
    (products || []).forEach(product => {
      if (product.storeCategory === collectionFilter && product.productType && !children.includes(product.productType)) {
        children.push(product.productType);
      }
    });

    setFilters(prev => ({
      ...prev,
      categories: [collectionFilter, ...children]
    }));
  }, [collectionFilter, products]);

  useEffect(() => {
    const onToggle = (e) => {
      const open = e?.detail?.open ?? !isOpen;
      if (open) {
        setMounted(true);
        setClosing(false);
        setIsOpen(true);
      } else {
        setClosing(true);
        setIsOpen(false);
        setTimeout(() => {
          setMounted(false);
          setClosing(false);
        }, 300);
      }
    };

    window.addEventListener('filter:toggle', onToggle);
    return () => window.removeEventListener('filter:toggle', onToggle);
  }, [isOpen]);

  const handleClose = () => {
    setClosing(true);
    setIsOpen(false);
    
    setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, 300);
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getProductCount = (filterType, value) => {
    return products.filter(product => {
      if (filterType === 'categories') {
        // Parent (storeCategory) or child (productType)
        if ((filterOptions.storeCategories || []).includes(value)) {
          return product.storeCategory === value;
        }
        return product.productType === value;
      }
      if (filterType === 'colours') return product.color === value;
      if (filterType === 'sleeves') return product.sleeves === value;
      if (filterType === 'length') return product.length === value;
      if (filterType === 'print') return product.print === value;
      if (filterType === 'pattern') return product.fabricPattern === value;
      if (filterType === 'material') return product.fabricType === value;
      if (filterType === 'collection') return product.category === value;
      return false;
    }).length;
  };

  const toggleFilter = (filterType, value, children = []) => {
    // Clear collection filter when user manually changes categories
    if (filterType === 'categories' && onClearCollectionFilter) {
      onClearCollectionFilter();
    }

    setFilters(prev => {
      const currentValues = prev[filterType];
      const isSelected = currentValues.includes(value);
      let newValues;
      if (isSelected) {
        // Deselect parent and all its children
        newValues = currentValues.filter(v => v !== value && !children.includes(v));
      } else {
        // Select parent and all its children
        newValues = [...new Set([...currentValues, value, ...children])];
      }
      return { ...prev, [filterType]: newValues };
    });
  };

  const toggleChildFilter = (filterType, value) => {
    // Clear collection filter when user manually changes categories or collection
    if ((filterType === 'categories' || filterType === 'collection') && onClearCollectionFilter) {
      onClearCollectionFilter();
    }

    setFilters(prev => {
      const currentValues = prev[filterType];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterType]: newValues };
    });
  };

  const clearAllFilters = () => {
    // Clear collection filter when clearing all filters
    if (onClearCollectionFilter) {
      onClearCollectionFilter();
    }

    setFilters({
      categories: [],
      colours: [],
      sleeves: [],
      length: [],
      print: [],
      pattern: [],
      material: [],
      priceRange: [minPrice, maxPrice],
      discountRange: [0, 100],
      collection: []
    });
  };

  const applyFilters = () => {
    if (onFilterChange) {
      onFilterChange(filters);
    }
    handleClose();
  };

  // Category hierarchy: parent = storeCategory, children = productTypes within that category
  const getCategoryHierarchy = () => {
    const hierarchy = {};
    (filterOptions.storeCategories || []).forEach(cat => {
      hierarchy[cat] = [];
    });
    (products || []).forEach(product => {
      const cat = product.storeCategory;
      const type = product.productType;
      if (cat && Object.prototype.hasOwnProperty.call(hierarchy, cat) && type && !hierarchy[cat].includes(type)) {
        hierarchy[cat].push(type);
      }
    });
    return hierarchy;
  };

  const categoryOptions = getCategoryHierarchy();
  // Render sidebar into document.body using a portal to avoid stacking-context issues
  if (!mounted) return null;

  const portalContent = (
    <div id="filter-root">
      <div
        id="filter-overlay"
        onClick={handleClose}
        style={{ zIndex: 2147483648, position: 'fixed', inset: 0 }}
        className="bg-black/40 font-bdogrotesk"
      />

      <div
        id="filter-panel"
        style={{ zIndex: 2147483649, position: 'fixed', top: 0, right: 0, bottom: 0 }}
        className={`w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden drawer-panel ${
          closing ? 'drawer-slide-out' : 'drawer-slide-in'
        }`}
      >
        {/* Header */}
        <div className="p-2 flex justify-between items-center">
          <h3 className="text-base font-medium font-bdogrotesk">Filters</h3>
          <div className="flex gap-2 items-center">
            <button
              onClick={clearAllFilters}
              className="text-sm font-bdogrotesk hover:underline"
            >
              Clear
            </button>
            <button
              onClick={handleClose}
              className="text-sm font-bdogrotesk hover:underline"
            >
              Close
            </button>
          </div>
        </div>

        {/* Applied Filters: show currently selected filters as removable pills */}
        <div className=" pb-2">
          <div className="flex flex-wrap gap-2">
            {filters.priceRange && filters.priceRange[1] < maxPrice && (
              <button
                key="applied-price"
                onClick={() => setFilters(prev => ({ ...prev, priceRange: [minPrice, maxPrice] }))}
                className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk"
              >
                <span>Up to ₹{filters.priceRange[1]}</span>
                <span className="text-base">×</span>
              </button>
            )}

            {filters.collection && filters.collection.map(col => (
              <button key={`applied-col-${col}`} onClick={() => toggleChildFilter('collection', col)} className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk">
                <span>{col}</span><span className="text-base">×</span>
              </button>
            ))}

            {filters.categories && filters.categories.map(cat => (
              <button
                key={`applied-cat-${cat}`}
                onClick={() => toggleChildFilter('categories', cat)}
                className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk"
              >
                <span>{cat}</span>
                <span className="text-base">×</span>
              </button>
            ))}

            {filters.colours && filters.colours.map(colr => (
              <button key={`applied-colr-${colr}`} onClick={() => toggleChildFilter('colours', colr)} className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk">
                <span>{colr}</span><span className="text-base">×</span>
              </button>
            ))}

            {filters.material && filters.material.map(m => (
              <button key={`applied-mat-${m}`} onClick={() => toggleChildFilter('material', m)} className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk">
                <span>{m}</span><span className="text-base">×</span>
              </button>
            ))}

            {filters.print && filters.print.map(p => (
              <button key={`applied-print-${p}`} onClick={() => toggleChildFilter('print', p)} className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk">
                <span>{p}</span><span className="text-base">×</span>
              </button>
            ))}

            {filters.pattern && filters.pattern.map(p => (
              <button key={`applied-pattern-${p}`} onClick={() => toggleChildFilter('pattern', p)} className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk">
                <span>{p}</span><span className="text-base">×</span>
              </button>
            ))}

            {filters.sleeves && filters.sleeves.map(s => (
              <button key={`applied-sleeves-${s}`} onClick={() => toggleChildFilter('sleeves', s)} className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk">
                <span>{s}</span><span className="text-base">×</span>
              </button>
            ))}

            {filters.length && filters.length.map(l => (
              <button key={`applied-length-${l}`} onClick={() => toggleChildFilter('length', l)} className="inline-flex items-center gap-2 px-2 py-1 border border-gray-300 text-sm font-bdogrotesk">
                <span>{l}</span><span className="text-base">×</span>
              </button>
            ))}
          </div>
        </div>

        {/* Scrollable Filter Content */}
        <div className="flex-1 overflow-y-auto no-scrollbar">
              {/* Category Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('category')}
                  className="w-full py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Category</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.category ? '×' : '+'}</span>
                </button>
                {openSections.category && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {Object.entries(categoryOptions).map(([parent, children]) => {
                      const parentCount = getProductCount('categories', parent);
                      const isParentSelected = filters.categories.includes(parent);

                      return (
                        <div key={parent} className="flex flex-col ">
                          {/* Parent category row */}
                          <label className="flex items-start sm:items-center justify-between gap-1 text-sm sm:text-base font-medium cursor-pointer">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isParentSelected}
                                onChange={() => toggleFilter('categories', parent, children)}
                                className="custom-checkbox"
                              />
                              <span className="font-bdogrotesk text-black underline">{parent}</span>
                            </div>
                            <span className="text-black font-bdogrotesk text-sm">{parentCount}</span>
                          </label>
                          {/* Child product types */}
                          {children.map(child => {
                            const count = getProductCount('categories', child);
                            return (
                              <label key={child} className="flex items-center justify-between gap-1 text-sm cursor-pointer ml-5">
                                <div className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={filters.categories.includes(child)}
                                    onChange={() => toggleChildFilter('categories', child)}
                                    className="custom-checkbox"
                                  />
                                  <span className="font-bdogrotesk font-medium text-black">{child}</span>
                                </div>
                                <span className="text-gray-400 font-bdogrotesk text-sm">{count}</span>
                              </label>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Colour Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('colour')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Colour</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.colour ? '×' : '+'}</span>
                </button>
                {openSections.colour && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {filterOptions.colours.map(colour => {
                      const count = getProductCount('colours', colour);
                      return (
                        <label key={colour} className="flex items-start sm:items-center justify-between gap-2 text-sm sm:text-base font-medium cursor-pointer">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={filters.colours.includes(colour)}
                              onChange={() => toggleChildFilter('colours', colour)}
                              className="custom-checkbox"
                            />
                            <span className="font-bdogrotesk underline text-black">{colour}</span>
                          </div>
                          <span className="text-gray-400 font-bdogrotesk text-sm sm:text-base font-medium">[{count}]</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Collection Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('collection')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Collection</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.collection ? '×' : '+'}</span>
                </button>
                {openSections.collection && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {filterOptions.collection.map(collection => {
                      const count = getProductCount('collection', collection);
                      return (
                        <label key={collection} className="flex items-start sm:items-center justify-between gap-2 text-sm sm:text-base font-medium cursor-pointer">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={filters.collection.includes(collection)}
                              onChange={() => toggleChildFilter('collection', collection)}
                              className="custom-checkbox"
                            />
                            <span className="font-bdogrotesk">{collection}</span>
                          </div>
                          <span className="text-gray-400 font-bdogrotesk text-sm sm:text-base font-medium">[{count}]</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Price Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('price')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Price</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.price ? '×' : '+'}</span>
                </button>
                {openSections.price && (
                  <div className=" pb-4 space-y-3">
                    <div className="relative">
                      {(() => {
                        const priceUpperBound = filters.priceRange[1];
                        const priceRangeSpan = Math.max(maxPrice - minPrice, 1);
                        const progressPercent = ((priceUpperBound - minPrice) / priceRangeSpan) * 100;
                        return (
                      <input
                        type="range"
                        min={minPrice}
                        max={maxPrice}
                        step="100"
                        value={priceUpperBound}
                        onChange={(e) => setFilters(prev => ({ ...prev, priceRange: [minPrice, parseInt(e.target.value)] }))}
                        className="custom-range"
                        style={{
                          width: '100%',
                          background: `linear-gradient(to right, black ${progressPercent}%, #e5e7eb ${progressPercent}%)`
                        }}
                      />
                        );
                      })()}
                    </div>
                    <div className="flex justify-center text-base font-medium font-bdogrotesk">
                      <span>Up to ₹{filters.priceRange[1]}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Discount Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('discount')}
                  className="w-full py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Discount</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.discount ? '×' : '+'}</span>
                </button>
                {openSections.discount && (
                  <div className=" pb-4 space-y-3">
                    <div className="text-base font-medium font-bdogrotesk">
                      {filters.discountRange[0]}% and above
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={filters.discountRange[0]}
                      onChange={(e) => setFilters(prev => ({ ...prev, discountRange: [parseInt(e.target.value), 100] }))}
                      className="custom-range"
                      style={{ width: '100%' }}
                    />
                  </div>
                )}
              </div>

              {/* Sleeves Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('sleeves')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Sleeves</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.sleeves ? '×' : '+'}</span>
                </button>
                {openSections.sleeves && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {filterOptions.sleeves.map(sleeve => {
                      const count = getProductCount('sleeves', sleeve);
                      return (
                        <label key={sleeve} className="flex items-start sm:items-center justify-between gap-2 text-sm sm:text-base font-medium cursor-pointer">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={filters.sleeves.includes(sleeve)}
                              onChange={() => toggleChildFilter('sleeves', sleeve)}
                              className="custom-checkbox"
                            />
                            <span className="font-bdogrotesk">{sleeve}</span>
                          </div>
                          <span className="text-gray-400 font-bdogrotesk text-sm sm:text-base font-medium">[{count}]</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Length Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('length')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Length</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.length ? '×' : '+'}</span>
                </button>
                {openSections.length && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {filterOptions.length.map(length => {
                      const count = getProductCount('length', length);
                      return (
                        <label key={length} className="flex items-start sm:items-center justify-between gap-2 text-sm sm:text-base font-medium cursor-pointer">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={filters.length.includes(length)}
                              onChange={() => toggleChildFilter('length', length)}
                              className="custom-checkbox"
                            />
                            <span className="font-bdogrotesk">{length}</span>
                          </div>
                          <span className="text-gray-400 font-bdogrotesk text-sm sm:text-base font-medium">[{count}]</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Material Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('material')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Material</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.material ? '×' : '+'}</span>
                </button>
                {openSections.material && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {filterOptions.material.map(material => {
                      const count = getProductCount('material', material);
                      return (
                        <label key={material} className="flex items-start sm:items-center justify-between gap-2 text-sm sm:text-base font-medium cursor-pointer">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={filters.material.includes(material)}
                              onChange={() => toggleChildFilter('material', material)}
                              className="custom-checkbox"
                            />
                            <span className="font-bdogrotesk">{material}</span>
                          </div>
                          <span className="text-gray-400 font-bdogrotesk text-sm sm:text-base font-medium">[{count}]</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Print Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('print')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Print</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.print ? '×' : '+'}</span>
                </button>
                {openSections.print && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {filterOptions.print.map(print => {
                      const count = getProductCount('print', print);
                      return (
                        <label key={print} className="flex items-start sm:items-center justify-between gap-2 text-sm sm:text-base font-medium cursor-pointer">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={filters.print.includes(print)}
                              onChange={() => toggleChildFilter('print', print)}
                              className="custom-checkbox"
                            />
                            <span className="font-bdogrotesk">{print}</span>
                          </div>
                          <span className="text-gray-400 font-bdogrotesk text-sm sm:text-base font-medium">[{count}]</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Pattern Filter */}
              <div className="border-b border-black w-[95%] mx-auto">
                <button
                  onClick={() => toggleSection('pattern')}
                  className="w-full  py-4 flex justify-between items-center font-bdogrotesk text-base hover:bg-gray-50"
                >
                  <span className="font-bdogrotesk text-3xl sm:text-4xl text-black font-semibold">Pattern</span>
                  <span className="font-bdogrotesk text-2xl text-black font-medium">{openSections.pattern ? '×' : '+'}</span>
                </button>
                {openSections.pattern && (
                  <div className=" pb-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {filterOptions.pattern.map(pattern => {
                      const count = getProductCount('pattern', pattern);
                      return (
                        <label key={pattern} className="flex items-start sm:items-center justify-between gap-2 text-sm sm:text-base font-medium cursor-pointer">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={filters.pattern.includes(pattern)}
                              onChange={() => toggleChildFilter('pattern', pattern)}
                              className="custom-checkbox"
                            />
                            <span className="font-bdogrotesk">{pattern}</span>
                          </div>
                          <span className="text-gray-400 font-bdogrotesk text-sm sm:text-base font-medium">[{count}]</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Apply Filters Button */}
            <div className="p-4 bg-white">
              <button
                type="button"
                onClick={applyFilters}
                className="group w-full bg-black text-white px-3 py-3 flex items-center justify-between text-[15px] font-bdogrotesk font-semibold tracking-wide box-border"
              >
                <div className="relative h-5 overflow-hidden w-full">
                  <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                    <span className="flex font-bdogrotesk h-5 items-center justify-start">
                      Apply Filters
                    </span>
                    <span className="flex font-bdogrotesk h-5 items-center justify-start">Apply Filters</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
  );

  return createPortal(portalContent, document.body);
}

// Debug: log stacking info when sidebar is mounted
if (typeof window !== 'undefined') {
  window.addEventListener('filter:debug', () => {
    const panel = document.getElementById('filter-panel');
    const navbar = document.querySelector('.navbar-logo-center-home');
    console.log('Filter panel computed z-index:', panel && window.getComputedStyle(panel).zIndex);
    console.log('Navbar computed z-index:', navbar && window.getComputedStyle(navbar).zIndex);
    console.log('Filter panel in DOM?', !!panel, 'Navbar in DOM?', !!navbar);
  });
}
